import React from 'react';
import { Box, Paper, Typography, Skeleton, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { BRAND_COLORS } from '../../styles/theme';
import type { KpiMetric } from '../../types/analytics';
import { formatMetricValue, trendPercent, KPI_INSIGHTS } from './kpiCard.utils';

const KpiCard: React.FC<{
  metric: KpiMetric;
  loading: boolean;
  invertTrend?: boolean;
  comparisonLabel?: string;
}> = ({ metric, loading, invertTrend = false, comparisonLabel }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 2.5, flex: '1 1 180px', minWidth: 160 }}>
        <Skeleton width="60%" height={20} />
        <Skeleton width="40%" height={36} sx={{ mt: 1 }} />
        <Skeleton width="50%" height={16} sx={{ mt: 0.5 }} />
      </Paper>
    );
  }

  const pct = trendPercent(metric.value, metric.previous_value);
  const isPositive = pct !== null && pct > 0;
  const isNeutral = pct === null || Math.abs(pct) < 0.5;
  // For bounce rate, down is good
  const isGood = invertTrend ? !isPositive : isPositive;
  const label = metric.format === 'string' ? 'Top Source' : metric.label;
  const insight = KPI_INSIGHTS[label] || '';

  return (
    <Tooltip
      title={insight}
      arrow
      placement="top"
      enterDelay={300}
      slotProps={{
        tooltip: { sx: { maxWidth: 260, fontSize: 12, lineHeight: 1.4 } },
      }}
    >
      <Paper sx={{ p: 2.5, flex: '1 1 180px', minWidth: 160, cursor: 'help' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mt: 0.5, color: BRAND_COLORS.forestGreen.main }}
        >
          {formatMetricValue(metric)}
        </Typography>
        {!isNeutral && pct !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            {isPositive ? (
              <TrendingUpIcon
                sx={{ fontSize: 16, color: isGood ? 'success.main' : 'error.main' }}
              />
            ) : (
              <TrendingDownIcon
                sx={{ fontSize: 16, color: isGood ? 'success.main' : 'error.main' }}
              />
            )}
            <Typography
              variant="caption"
              sx={{ color: isGood ? 'success.main' : 'error.main', fontWeight: 600 }}
            >
              {Math.abs(pct).toFixed(1)}%
            </Typography>
            {comparisonLabel && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {comparisonLabel}
              </Typography>
            )}
          </Box>
        )}
        {isNeutral && metric.previous_value !== null && metric.format !== 'string' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <TrendingFlatIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              No change
            </Typography>
          </Box>
        )}
      </Paper>
    </Tooltip>
  );
};

export default KpiCard;
